/* eslint-disable @typescript-eslint/no-explicit-any */
import AuthApi from '@portal/repositories/auth';
import * as MessageService from '~/services/message';
import NavigationService from '~/services/navigation';
import * as StorageService from '~/services/storage';

import { TokenMethod } from '@portal/enum/device';
import ErrorsMessages from '@portal/enum/errors';
import { CreateTopupRequestDto } from '@portal/models/module';
import DeviceApi from '@portal/repositories/device';
import { setHeaderDevice } from '@portal/utils/device';
import {
  BLOCK_DEVICE,
  DELETE_DEVICE,
  DELETE_DEVICE_ERROR,
  GET_DEVICE_COUNT,
  GET_DEVICE_COUNT_ERROR,
  GET_MY_DEVICES,
  GET_MY_DEVICES_ERROR,
  GET_REGISTER_INFORMATION,
  GET_REGISTER_INFORMATION_ERROR,
  SET_CARRIERS,
  SET_CARRIER_PRODUCT_LIST,
  SET_DEVICE_ERROR,
  SET_RECHARGE_HISTORY,
} from './actionTypes';
import * as AuthActions from './auth';
import { decreaseLoading, increaseLoading } from './loading';

export const registerDevice =
  (userId: string, method: TokenMethod) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const deviceId = StorageService.getOrCreateDeviceId();

      setHeaderDevice(deviceId);
      await AuthApi.requestSms(userId, method);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.toLowerCase() === ErrorsMessages.MAX_DEVICES_PER_USER) {
          MessageService.error('MAX_DEVICES_REACHED');

          dispatch(increaseLoading());
          await new Promise((resolve) => setTimeout(resolve, 2000));
          dispatch(decreaseLoading());
          await dispatch(AuthActions.logout());
          NavigationService.navigate('/');
          return;
        }
        dispatch(setDeviceError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getRegisterInformation = () => async (dispatch: any) => {
  dispatch(increaseLoading());
  try {
    const response = await DeviceApi.getRegisterInformation();
    dispatch({
      type: GET_REGISTER_INFORMATION,
      payload: response,
    });

    return response;
  } catch (error) {
    dispatch({
      type: GET_REGISTER_INFORMATION_ERROR,
      payload: error,
    });
    throw error;
  } finally {
    dispatch(decreaseLoading());
  }
};

export const setDeviceError = (error: Error | null) => ({
  type: SET_DEVICE_ERROR,
  payload: error,
});

export const getMyDevices =
  (isBlockedDevices?: boolean) => async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const payload = isBlockedDevices
        ? await DeviceApi.getMyBlockedDevices()
        : await DeviceApi.getMyDevices();
      dispatch({
        type: GET_MY_DEVICES,
        payload,
      });
    } catch (error) {
      dispatch({
        type: GET_MY_DEVICES_ERROR,
        payload: error,
      });
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getUserDevices = (userId: string) => async (dispatch: any) => {
  dispatch(increaseLoading());
  try {
    const payload = await DeviceApi.getUserDevices(userId);
    dispatch({
      type: GET_MY_DEVICES,
      payload,
    });
  } catch (error) {
    dispatch({
      type: GET_MY_DEVICES_ERROR,
      payload: error,
    });
  } finally {
    dispatch(decreaseLoading());
  }
};

export const deleteDevice =
  (deviceId: string, callback: (err: Error | null) => void) =>
  async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const payload = await DeviceApi.deleteDevice(deviceId);
      dispatch({
        type: DELETE_DEVICE,
        payload,
      });
      callback(null);
    } catch (error) {
      if (error instanceof Error) callback(error);
      dispatch({
        type: DELETE_DEVICE_ERROR,
        payload: error,
      });
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const unlockDevice =
  (deviceId: string, callback: (err: Error | null) => void) =>
  async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const payload = await DeviceApi.unlockDevice(deviceId);
      dispatch({
        type: DELETE_DEVICE,
        payload,
      });
      callback(null);
    } catch (error) {
      if (error instanceof Error) callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const blockDevice =
  (deviceId: string, callback: (err: Error | null) => void) =>
  async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const payload = await DeviceApi.blockDevice(deviceId);
      dispatch({
        type: BLOCK_DEVICE,
        payload,
      });
      callback(null);
    } catch (error) {
      if (error instanceof Error) callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getDeviceCount = () => async (dispatch: any) => {
  dispatch(increaseLoading());
  try {
    const payload = await DeviceApi.getDeviceCount();
    dispatch({
      type: GET_DEVICE_COUNT,
      payload,
    });
  } catch (error) {
    dispatch({
      type: GET_DEVICE_COUNT_ERROR,
      payload: error,
    });
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getRechargeCarriers =
  (areaCode: string, callback: (err: Error | null) => void) =>
  async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const carriers = await DeviceApi.getCarriers(areaCode);

      dispatch({
        type: SET_CARRIERS,
        payload: carriers,
      });
      callback(null);
    } catch (err) {
      if (err instanceof Error) {
        callback(err);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getCarrierProductList =
  (
    areaCode: string,
    carrierId: string,
    callback: (err: Error | null) => void
  ) =>
  async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const carrierProductList = await DeviceApi.getCarrierProductList(
        areaCode,
        carrierId
      );

      dispatch({
        type: SET_CARRIER_PRODUCT_LIST,
        payload: carrierProductList,
      });
      callback(null);
    } catch (err) {
      if (err instanceof Error) {
        callback(err);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getRechargeHistory =
  (callback: (err: Error | null) => void) => async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const history = await DeviceApi.getRechargeHistory();

      dispatch({
        type: SET_RECHARGE_HISTORY,
        payload: history,
      });

      callback(null);
    } catch (err) {
      if (err instanceof Error) {
        callback(err);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const rechargeTransaction =
  (
    body: CreateTopupRequestDto,
    pin: string,
    callback: (
      err: Error | null,
      receipt: models.RechargeReceipt | null
    ) => void
  ) =>
  async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const response = await DeviceApi.rechargeTransaction(body, pin);

      callback(null, response?.receipt);
    } catch (err) {
      if (err instanceof Error) {
        callback(err, null);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getRecharges =
  (params: advancedFilterModels.RechargeMobileFilter) =>
  async (dispatch: any) => {
    dispatch(increaseLoading());
    try {
      const response = await DeviceApi.getRecharges({
        ...(params
          ? {
              ...params,
              offset: params.page,
              limit: params.pageSize,
            }
          : {
              pageSize: 10,
              page: 1,
              orderBy: 'createdAt',
              sort: 'desc',
            }),
        offset: params?.page,
        limit: params?.pageSize,
      });
      dispatch({
        type: SET_RECHARGE_HISTORY,
        payload: response,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
